/**
 * cas-bdca / cas-2c1b (epic cas-c50b): helpers for sign-in links (`?k=<token>` on the real page).
 *
 * The backend contract (apps/backend/src/auth/sign-in-link, cas-f8a0):
 *   POST /api/v1/auth/link-sign-in/exchange { token }
 *     200 { customToken, destinationPath, workspaceId }
 *     410 { code: 'LINK_SIGN_IN_UNAVAILABLE', destinationPath? }   dead link
 *     429 rate limited, 503 retryable
 */
import type { RouteLocationRaw } from 'vue-router';
import { LINK_LOADER_ID, LINK_TOKEN_STORAGE_KEY } from '~/utils/link-token-inline-script';

export const SIGN_IN_LINK_EXCHANGE_PATH = '/api/v1/auth/link-sign-in/exchange';
export const SIGN_IN_LINK_ERROR_CODE = 'LINK_SIGN_IN_UNAVAILABLE';
/** Query flag /sign-in reads to show the one-line "link expired" notice. */
export const LINK_EXPIRED_NOTICE = 'link-expired';

export interface SignInLinkExchange {
  customToken: string;
  destinationPath: string;
  workspaceId: string | null;
}

export type ExchangeOutcome =
  | { kind: 'ok'; exchange: SignInLinkExchange }
  | { kind: 'dead'; destinationPath: string | null }
  | { kind: 'retry' };

/**
 * A same-origin, path-only destination. The backend allowlists destinations
 * already; this is the client's own refusal of anything that could leave the
 * site (`//host`, `/\\host`, absolute URLs) if that ever regressed.
 */
export function safeDestination(path: unknown): string | null {
  if (typeof path !== 'string' || path.length === 0 || path.length > 512) return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return null;
  return path;
}

/** Where a dead link sends the user: /sign-in, keeping the destination as returnUrl. */
export function deadLinkTarget(destinationPath: string | null): RouteLocationRaw {
  const returnUrl = safeDestination(destinationPath);
  return {
    path: '/sign-in',
    query: returnUrl ? { returnUrl, notice: LINK_EXPIRED_NOTICE } : { notice: LINK_EXPIRED_NOTICE }
  };
}

/**
 * Where "Sign in instead" goes from the retry state: /sign-in keeping the
 * destination, with NO expired notice — the link may still work.
 */
export function signInInsteadTarget(destinationPath: string | null): RouteLocationRaw {
  const returnUrl = safeDestination(destinationPath);
  return returnUrl ? { path: '/sign-in', query: { returnUrl } } : { path: '/sign-in' };
}

/**
 * The uid a Firebase custom token signs in as. Custom tokens are JWTs with a
 * top-level `uid` claim. The payload is read, not verified: this only decides
 * whether the link belongs to the account already signed in. Firebase verifies
 * the token when it is actually used.
 */
export function customTokenUid(customToken: string): string | null {
  try {
    const payload = customToken.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { uid?: unknown };
    return typeof claims.uid === 'string' && claims.uid.length > 0 ? claims.uid : null;
  } catch {
    return null;
  }
}

/** Classify an exchange failure. Never includes the token in anything it returns. */
export function classifyExchangeError(error: unknown): ExchangeOutcome {
  const err = error as {
    status?: number;
    statusCode?: number;
    response?: { status?: number };
    data?: { code?: unknown; destinationPath?: unknown; context?: { destinationPath?: unknown } };
  };
  const status = err?.status ?? err?.statusCode ?? err?.response?.status;
  if (status === 410 || err?.data?.code === SIGN_IN_LINK_ERROR_CODE) {
    // The backend's global exception filter forwards extra fields only inside
    // `context`; the top-level field is kept for any caller that bypasses it.
    const destination = err?.data?.context?.destinationPath ?? err?.data?.destinationPath;
    return { kind: 'dead', destinationPath: safeDestination(destination) };
  }
  // 400/404 would mean the request itself is wrong; treat it as a dead link
  // rather than inviting a retry loop the user cannot fix.
  if (status === 400 || status === 404) return { kind: 'dead', destinationPath: null };
  // 429, 5xx and network failures are worth one more tap.
  return { kind: 'retry' };
}

/** Validate a 200 exchange body. */
export function parseExchange(body: unknown): SignInLinkExchange | null {
  const data = body as {
    customToken?: unknown;
    destinationPath?: unknown;
    workspaceId?: unknown;
  } | null;
  const destinationPath = safeDestination(data?.destinationPath);
  if (!data || typeof data.customToken !== 'string' || !data.customToken || !destinationPath) {
    return null;
  }
  return {
    customToken: data.customToken,
    destinationPath,
    workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : null
  };
}

/**
 * A sign-in link token wherever it appears inside a string: the `k` query
 * parameter, raw (`?k=` / `&k=`) or URL-encoded (`%3Fk%3D` / `%26k%3D`).
 * Tokens are 22 base64url characters (SIGN_IN_LINK_TOKEN_PATTERN in the
 * backend); 16+ is matched so a token format change still redacts.
 */
const LINK_TOKEN_IN_TEXT = /([?&]k=|%3Fk%3D|%26k%3D)[A-Za-z0-9_-]{16,}/gi;

export const REDACTED_LINK_TOKEN = 'redacted';

/** Replace every sign-in link token inside a string. */
export function redactLinkTokens(text: string): string {
  return text.replace(LINK_TOKEN_IN_TEXT, `$1${REDACTED_LINK_TOKEN}`);
}

/**
 * Deep copy-on-write redaction of sign-in link tokens from an analytics
 * payload (event properties, `$set_once` initial-URL person properties,
 * session-replay snapshot hrefs). Returns the SAME reference when nothing
 * matched, so the common case allocates nothing.
 */
export function redactSignInLinkTokens<T>(value: T, depth = 0): T {
  if (typeof value === 'string') {
    if (!value.includes('k=') && !/k%3D/i.test(value)) return value;
    const redacted = redactLinkTokens(value);
    return (redacted === value ? value : redacted) as T;
  }
  if (depth > 12 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    let out: unknown[] | null = null;
    value.forEach((item, index) => {
      const next = redactSignInLinkTokens(item, depth + 1);
      if (next !== item) {
        out ??= value.slice();
        out[index] = next;
      }
    });
    return (out ?? value) as T;
  }
  let out: Record<string, unknown> | null = null;
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    const next = redactSignInLinkTokens(item, depth + 1);
    if (next !== item) {
      out ??= { ...(value as Record<string, unknown>) };
      out[key] = next;
    }
  }
  return (out ?? value) as T;
}

/** The token parked by the inline <head> script (utils/link-token-inline-script.ts). */
export interface StashedLink {
  token: string;
  /** Pathname of the page the link opened; the token belongs to that page only. */
  path: string | null;
}

export function readStashedLink(): StashedLink | null {
  try {
    const raw = sessionStorage.getItem(LINK_TOKEN_STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as { t?: unknown; p?: unknown };
      if (typeof parsed?.t === 'string' && parsed.t) {
        return { token: parsed.t, path: typeof parsed.p === 'string' ? parsed.p : null };
      }
    } catch {
      // A bare token (older script): not bound to a page.
    }
    return { token: raw, path: null };
  } catch {
    return null;
  }
}

export function readStashedLinkToken(): string | null {
  return readStashedLink()?.token ?? null;
}

export function clearStashedLinkToken(): void {
  try {
    sessionStorage.removeItem(LINK_TOKEN_STORAGE_KEY);
  } catch {
    // nothing to clear
  }
}

/** Remove the inline script's pre-mount "Opening your link" screen, if it is up. */
export function removeLinkLoader(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(LINK_LOADER_ID)?.remove();
}

/**
 * cas-f212: where "Sign in" goes from a bad email sign-in link (/public/auth/SignIn). A link
 * can't be re-requested by the person, so the way forward is the normal sign-in, with the
 * email prefilled when the page knows it and the link's destination kept.
 */
export function signInLinkRecoveryTarget(args: {
  email: string | null | undefined;
  returnUrl: string | null | undefined;
}): RouteLocationRaw {
  const query: Record<string, string> = {};
  const email = args.email?.trim();
  if (email) query.email = email;
  const returnUrl = safeDestination(args.returnUrl ?? null);
  if (returnUrl) query.returnUrl = returnUrl;
  return { path: '/sign-in', query };
}
